import store from '../store/index'
import TimeManager from "./TimeManager";
import * as XLSX from "xlsx";
import { openLocalXlsx } from "@/Services/docOpeners";

function formatNumber(decimalCount, amount, symbol, thousands = ",", decimal = ".") {
    try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";

        // Convert the amount to a fixed-point notation
        amount = Number(amount).toFixed(decimalCount);
        if(isNaN(amount)) return 0;

        // Use regex to remove unnecessary trailing zeros and the decimal point if it is an integer
        let [integerPart, fractionalPart] = amount.split('.');

        // Trimming the fractional part of trailing zeros if necessary
        if (fractionalPart) {
            fractionalPart = fractionalPart.replace(/0+$/, '');
        }

        let i = Math.abs(parseInt(integerPart)).toString(); // Ensure the integer part is positive for the split
        let j = i.length > 3 ? i.length % 3 : 0;

        return (
            symbol +
            ' ' +
            negativeSign +
            (j ? i.substr(0, j) + thousands : '') +
            i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
            (fractionalPart ? decimal + fractionalPart : '')
        ).trimStart();
    } catch (e) {
        console.error(e);
    }
}

export const utils = {
    /**
     * Converts a duration in minutes to a compact human-readable string like
     * "2days 3hours and 15mins".
     * - Omits zero-value units.
     * - Uses singular for 1day/1hour; minutes always use "mins" as per requirement.
     * - Invalid, NaN, or negative inputs return "0mins".
     * @param {number|string} totalMins - Total minutes to format.
     * @returns {string}
     */
    formatDurationFromMinutes(totalMins) {
        const minsNum = Number(totalMins);
        if (!isFinite(minsNum) || minsNum < 0) return '0mins';

        const total = Math.floor(minsNum);
        const days = Math.floor(total / (60 * 24));
        const hours = Math.floor((total % (60 * 24)) / 60);
        const minutes = Math.floor(total % 60);

        const parts = [];
        if (days) parts.push(days + (days === 1 ? 'day' : 'days'));
        if (hours) parts.push(hours + (hours === 1 ? 'hour' : 'hours'));
        if (minutes || parts.length === 0) parts.push(minutes + 'mins');

        if (parts.length > 1) {
            const last = parts.pop();
            return parts.join(' ') + ' and ' + last;
        }
        return parts[0];
    },
    formatAbbreviatedNumber(num, decimalPlaces = 1) {
        if (num === null || num === undefined || isNaN(num)) {
            return '0';
        }

        // Convert to absolute value for calculation
        const absNum = Math.abs(num);
        const sign = num < 0 ? '-' : '';

        if (absNum >= 1000000000) {
            return sign + (absNum / 1000000000).toFixed(decimalPlaces) + 'B';
        } else if (absNum >= 1000000) {
            return sign + (absNum / 1000000).toFixed(decimalPlaces) + 'M';
        } else if (absNum >= 1000) {
            return sign + (absNum / 1000).toFixed(decimalPlaces) + 'K';
        } else {
            return sign + absNum.toFixed(decimalPlaces);
        }
    },

    toPascalCase(string) {
        return string
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
            .replace(/^\w/, s => s.toUpperCase());
    },
    cutLength(string, limit = 45) {
        if (string.length < limit) {
            return string
        } else {
            return string.toString().substring(0, limit).concat('...')
        }
    },
    formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",", isCurrency = true) {
        amount = this.roundAmount(amount, decimalCount);
        if (!isCurrency) {
            return formatNumber(decimalCount, amount, '', thousands, decimal);
        }
        let currency = "";
        return formatNumber(decimalCount, amount, currency, thousands, decimal);
    },

    formatCurrency(amount, decimalCount = 2, currency) {
        if (!currency) currency = ``;
        return formatNumber(decimalCount, amount, currency);
    },
    formatDefaultCurrency(amount, decimalCount = 2) {
        let currency = "";
        let shop = store.getters.getStore;
        if (shop && shop.StoreName !== 'All') {
            currency = store.getters.getBaseCurrency.Symbol;
        }
        return formatNumber(decimalCount, amount, currency);
    },

    formatQTY(amount) {
        amount = this.roundAmount(amount, 3);
        return this.formatMoney(amount, 3, '.', ',', false);
    },

    /**
     * Strip the time component from a Timeline-normalised "yyyy-MM-dd HH:mm:ss"
     * string. Returns the bare "yyyy-MM-dd". Returns '' for null/non-string.
     *
     * Used by historical-value callers because the Timeline normalises
     * `timeFrame.to` / `timeFrame.from` to a full datetime, but the date
     * picker and the `/api/v2/stock/historical-value` bare-date path both
     * want date-only. Bug history: failing to strip caused a duplicated-time
     * "2026-09-15 23:59:59 23:59:00" payload and a 400 Bad Request.
     */
    dateOnly(s) {
        if (typeof s !== 'string') return '';
        const i = s.indexOf(' ');
        return i > 0 ? s.substring(0, i) : s;
    },

    roundAmount(amount, decimalPlaces = 2) {
        if (!amount) return 0;
        if (typeof amount === 'string') {
            amount = amount.replace(`,`, ``);
            amount = Number(amount);
        }
        amount = amount.toFixed(decimalPlaces);
        return Number(amount);
    },

    removeIf(arr, predicate) {
        return arr.filter(predicate);
    },
    remove(arr, obj) {
        return arr.filter(v => v !== obj)
    },

    displayDate(date, isDateTime = true) {
        return TimeManager.shortenDisplay(date, isDateTime);
    },

    displayJavaDate(date) {
        return TimeManager.shortJavaDateTimeObject(date);
    },

    getNow() {
        let date = new Date();
        return date.toISOString().split('T')[0];
    },

    showLoading: () => store.commit(`setLoadingState`, true),
    hideLoading: () => store.commit(`setLoadingState`, false),

    /**
     * Export data to Excel file
     * @param {Array|Object} data - The data to export (array of objects, array of arrays, or object with rows property)
     * @param {String} fileName - The name of the file to export (without extension)
     * @param {String} sheetName - The name of the sheet in the Excel file
     * @param {Boolean} autoFormat - Whether to automatically format the data (default: true)
     */
    async exportToExcel(data, fileName = 'export', sheetName = 'Sheet1', autoFormat = true) {
        try {
            // Show loading indicator
            this.showLoading();

            // Keys related to internal store database identifiers that must never appear in exports
            const SENSITIVE_EXPORT_KEYS = ['storedb', 'store_db', 'db', 'database', 'databasename', 'schema'];
            const shouldExcludeKey = (key) => {
                if (!key) return false;
                const k = String(key).toLowerCase();
                return SENSITIVE_EXPORT_KEYS.includes(k);
            };
            const sanitizeObject = (obj) => {
                if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
                const out = {};
                for (const key in obj) {
                    if (shouldExcludeKey(key)) continue; // skip db-related keys
                    out[key] = obj[key];
                }
                return out;
            };

            let worksheet;
            let dataToExport = data;

            // Handle different data formats
            if (Array.isArray(data)) {
                if (data.length === 0) {
                    console.warn('No data to export');
                    this.hideLoading();
                    return;
                }

                // Check if it's an array of arrays (aoa) or array of objects (json)
                if (Array.isArray(data[0])) {
                    // For AoA, we cannot reliably detect headers -> pass through as-is
                    worksheet = XLSX.utils.aoa_to_sheet(data);
                } else {
                    // Format currency and number values if autoFormat is true
                    if (autoFormat) {
                        dataToExport = data.map(item => {
                            const source = sanitizeObject(item);
                            const formattedItem = {};
                            for (const key in source) {
                                if (typeof source[key] === 'number') {
                                    // Check if it might be a currency value (based on column name)
                                    const isCurrency = /price|cost|value|total|amount|balance|profit|revenue|sale/i.test(key);
                                    if (isCurrency) {
                                        formattedItem[key] = this.formatDefaultCurrency(source[key]);
                                    } else {
                                        formattedItem[key] = this.formatMoney(source[key], 2, '.', ',', false);
                                    }
                                } else {
                                    formattedItem[key] = source[key];
                                }
                            }
                            return formattedItem;
                        });
                    } else {
                        // Ensure raw objects are sanitized
                        dataToExport = data.map(sanitizeObject);
                    }

                    worksheet = XLSX.utils.json_to_sheet(dataToExport);
                }
            } else if (data && typeof data === 'object') {
                // Handle objects with rows property (like vue-good-table data)
                if (data.rows && Array.isArray(data.rows)) {
                    if (data.rows.length === 0) {
                        console.warn('No data to export');
                        this.hideLoading();
                        return;
                    }

                    // Format data if autoFormat is true
                    if (autoFormat) {
                        dataToExport = data.rows.map(item => {
                            const source = sanitizeObject(item);
                            const formattedItem = {};
                            for (const key in source) {
                                if (typeof source[key] === 'number') {
                                    // Check if it might be a currency value (based on column name)
                                    const isCurrency = /price|cost|value|total|amount|balance|profit|revenue|sale/i.test(key);
                                    if (isCurrency) {
                                        formattedItem[key] = this.formatDefaultCurrency(source[key]);
                                    } else {
                                        formattedItem[key] = this.formatMoney(source[key], 2, '.', ',', false);
                                    }
                                } else {
                                    formattedItem[key] = source[key];
                                }
                            }
                            return formattedItem;
                        });
                    } else {
                        dataToExport = data.rows.map(sanitizeObject);
                    }

                    worksheet = XLSX.utils.json_to_sheet(dataToExport);
                } else {
                    console.error('Invalid data format for export');
                    this.hideLoading();
                    return;
                }
            } else {
                console.error('Invalid data format for export');
                this.hideLoading();
                return;
            }

            // Create workbook and append worksheet
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            // Write to file using native Android opener on device, download elsewhere
            const ab = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            await openLocalXlsx(ab, `${fileName}.xlsx`);

            // Hide loading indicator
            this.hideLoading();
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            this.hideLoading();
        }
    }
}
